import { AgentRunDto } from "@growth/dto";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "./api";

export const isWorking = (status: string | undefined) =>
  status === "queued" || status === "running";

/** Everything the agents are doing in this workspace right now. Polls faster while busy. */
export const useActiveRuns = (enabled: boolean) =>
  useQuery({
    queryKey: ["agent-runs", "active"],
    queryFn: async () => (await api.get<AgentRunDto[]>("/agent-runs/active")).data,
    enabled,
    refetchInterval: (query) => (query.state.data?.length ? 2000 : 15_000),
  });

/** One run, followed live while it works. */
export const useAgentRun = (id: string | null | undefined) =>
  useQuery({
    queryKey: ["agent-runs", id],
    queryFn: async () => (await api.get<AgentRunDto>(`/agent-runs/${id}`)).data,
    enabled: Boolean(id),
    refetchInterval: (query) =>
      !query.state.data || isWorking(query.state.data.status) ? 1500 : false,
  });

/** Where in the app a run's work shows up, if it's for the brand being looked at. */
export const runLink = (run: AgentRunDto): string | null => {
  if (run.pieceId) return `/content/${run.pieceId}`;
  const byAgent: Record<string, string> = {
    researcher:
      run.title.startsWith("Set up") || run.title.startsWith("Redo") ? "/setup" : "/research",
    brand_designer: "/setup",
    seo_strategist: "/seo",
    icp_builder: "/leads/icp",
    director: "/calendar",
    trial: "/agents",
  };
  return byAgent[run.agent] ?? null;
};

/** Re-renders every second while `on`, for live timers. */
export const useTicking = (on: boolean) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [on]);
  return now;
};

export const clock = (ms: number) => {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return seconds < 60
    ? `${seconds} s`
    : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

/** "Working · 1:24", "Finished in 2:31", "Stopped". */
export const runStatus = (run: AgentRunDto, now: number) => {
  if (run.status === "queued") return "Waiting to start";
  const start = Date.parse(run.startedAt ?? run.createdAt);
  if (run.status === "running") return `Working · ${clock(now - start)}`;
  if (run.status === "succeeded" && run.endedAt)
    return `Finished in ${clock(Date.parse(run.endedAt) - start)}`;
  if (run.status === "failed") return "Stopped";
  return run.status;
};
